import buttonShapes from "@insite/mobius/Button/buttonShapes";
import buttonTypes from "@insite/mobius/Button/buttonTypes";
import hoverAnimations from "@insite/mobius/Button/hoverAnimations";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { COLORS, ELEMENTS } from "@tSurya/theme/design";
import { transitionHoverStyles } from "@tSurya/theme/mixins/transition";
import { css } from "styled-components";

export const buttonDisabledStyles = (buttonType: string) => {
    switch (buttonType) {
        case "solid":
            return css`
                &:disabled {
                    color: ${COLORS.white};
                    background-color: ${COLORS.black};
                    border-color: ${COLORS.black};
                    hover{
                        background-color: ${COLORS.disabled.main};
                        border-color: ${COLORS.disabled.main};
                    }
                }
            `;
        case "outline":
            return css`
                &:disabled {
                    color: ${COLORS.black};
                    background-color: ${COLORS.white};
                    border-color: ${COLORS.black};
                    hover{
                        color: ${COLORS.disabled.main};
                        border-color: ${COLORS.disabled.main};
                    }
                }
            `;
        default:
            break;
    }
};

export const buttonBaseStyles = (buttonType: string) => {
    const isMgbw = isMgbwWebsite();
    const baseStyle = css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: ${ELEMENTS.button.base.padding};
        border-radius: ${ELEMENTS.button.base.borderRadius};

/* identical to box height, or 200% */
        ${transitionHoverStyles()}
        ${isMgbw
            ? css`
                  &:focus,
                  &:focus-visible,
                  &:active {
                      outline: none;
                      -webkit-tap-highlight-color: transparent;
                  }
              `
            : null}
    `;

    const tertiaryStyle = css`
        color: ${COLORS.secondary.base};
    `;

    const textButtonStyle = css`
        padding: 0;
        background-color: transparent;
        letter-spacing: ${ELEMENTS.button.link.letterSpacing || "normal"};
    `;

    switch (buttonType) {
        case "solid":
            return css`
                ${baseStyle}
                ${buttonDisabledStyles(buttonType)}
            `;
        case "outline":
            return css`
                ${baseStyle}
                ${buttonDisabledStyles(buttonType)}
            `;
        case "tertiary":
            return css`
                ${baseStyle}
                ${buttonDisabledStyles(buttonType)}
                ${tertiaryStyle}
            `;
        case "text":
            return css`
                ${baseStyle}
                ${textButtonStyle}
            `;
        default:
            return null;
    }
};

export interface StoreThemeButtonProps {
    variant?: string;
    type?: string;
    shape?: string;
    animation?: string;
}

export const themeButtonStyles = ({ variant = "primary", type, shape, animation }: StoreThemeButtonProps) => {
    return css`
        ${({ theme }) => {
            const _buttonVariantProps = theme.button[variant];

            if (_buttonVariantProps) {
                const _buttonTypes: any = buttonTypes;
                const _buttonShapes: any = buttonShapes;
                const _hoverAnimations: any = hoverAnimations;

                const _buttonTypeFunction =
                    (type ? _buttonTypes[type] : _buttonTypes[_buttonVariantProps.buttonType]) || null;
                const _buttonShapeFunction =
                    (shape ? _buttonShapes[shape] : _buttonShapes[_buttonVariantProps.shape]) || null;
                const _hoverAnimationFunction =
                    (animation ? _hoverAnimations[animation] : _hoverAnimations[_buttonVariantProps.hoverAnimation]) ||
                    null;

                const buttonProps = {
                    ..._buttonVariantProps,
                    _color: _buttonVariantProps.color,
                    _sizeVariant: _buttonVariantProps.sizeVariant,
                    _size: _buttonVariantProps.size,
                    _shape: _buttonVariantProps.shape,
                    theme,
                };

                return css`
                    ${_buttonTypeFunction ? _buttonTypeFunction(buttonProps) : null}
                    ${_buttonShapeFunction ? _buttonShapeFunction(buttonProps) : null}
                    ${_hoverAnimationFunction ? _hoverAnimationFunction(buttonProps) : null}
                `;
            }

            return null;
        }}
    `;
};
